/**
 * GlobalState.netRoute 切片（任务 14.39）：访问路线开关的主线程镜像。
 * 事实源是 Worker 全局库 kv（`worker/net/route.ts`），本切片供设置页的开关态、
 * 以及**所有拼 API 域名的直链**（登录页协议页链接）消费。
 *
 * 与 config 切片的两处刻意差异：
 * - **不按 auth 门控**：登录页就要显示并可切（默认域名被墙时登不进去，旧版登录页
 *   同样挂了这个开关），所以 connect 时立即拉一次，登出也不清空。
 * - **写路径有返回值**：切换要连带重取 config + 重连 WS（Worker 侧编排），耗时
 *   取决于网络，所以 setOverseas 返回 Promise 供 UI 显示 busy 态，不乐观写——
 *   切错了用户就连不上，必须等 Worker 确认落位。
 *
 * 全程零 HTTP 的只有读（kv）；写在值真变时会带 1 次 GET /front/config（requests 注）。
 */

import type { GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type { ApiRouteState } from '~/worker/contract';

/** null = 未加载（Worker 未回包）。UI 此时按「国内路线」占位显示即可 */
export type NetRouteSlice = ApiRouteState | null;

export const NET_ROUTE_INITIAL: NetRouteSlice = null;

export interface NetRouteConnection {
  /**
   * 切换路线。resolve = Worker 已落 kv 并重连（切换失败抛 RpcError，UI 出错提示）。
   * 返回落位后的状态；`overseas` 与现值相同时是幂等空操作（Worker 侧跳过重连）。
   */
  setOverseas(overseas: boolean): Promise<ApiRouteState>;
  /** Worker 崩溃重建后重拉（kv 是事实源，内存值已随旧 Worker 消失） */
  resync(): void;
  dispose(): void;
}

export function connectNetRoute(store: GlobalStore, host: AppWorkerHost): NetRouteConnection {
  let epoch = 0;

  function load(): void {
    const seq = ++epoch;
    void host.call('net/route/get', null).then(
      (state) => {
        if (seq === epoch) store.set({ netRoute: state });
      },
      () => undefined, // 失败保持现值（null→占位国内）；resync 兜底
    );
  }
  load();

  return {
    async setOverseas(overseas) {
      const seq = ++epoch; // 在途的 get 不再覆盖本次结果
      const state = await host.call('net/route/set', { overseas });
      if (seq === epoch) store.set({ netRoute: state });
      return state;
    },
    resync: load,
    dispose() {
      epoch += 1; // 在途响应过期
    },
  };
}
